import { useId, useState } from 'react';

import { Button } from './Button';
import { Input } from './Input';

export interface ApiRequest {
  system: string;
  permission: 'read' | 'read-write';
  expiry: string;
}

export interface SecretsApprovalWidgetProps {
  summary: string;
  apiRequests: ApiRequest[];
  justification: string;
  mfaRequired?: boolean;
  status?: 'pending' | 'approved' | 'denied';
  denialMessage?: string;
  onApprove?: () => void;
  onDeny?: () => void;
  onMfaSubmit?: (code: string) => void;
  className?: string;
}

export function SecretsApprovalWidget({
  summary,
  apiRequests,
  justification,
  mfaRequired = false,
  status = 'pending',
  denialMessage,
  onApprove,
  onDeny,
  onMfaSubmit,
  className = '',
}: SecretsApprovalWidgetProps) {
  const [expanded, setExpanded] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const titleId = useId();

  if (status === 'approved') {
    return (
      <div className={`p-sd-2 rounded-sd-md bg-sd-success/10 w-full ${className}`.trim()}>
        <div className="flex items-center gap-sd-1">
          <svg className="w-4 h-4 text-sd-success shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 8.5L6.5 12L13 4" />
          </svg>
          <span aria-live="polite" className="text-sd-sm text-sd-success">Access granted — credentials scoped to this plan</span>
        </div>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className={`p-sd-2 rounded-sd-md bg-sd-error/10 w-full ${className}`.trim()}>
        <div className="flex items-center gap-sd-1">
          <svg className="w-4 h-4 text-sd-error shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 4L12 12M12 4L4 12" />
          </svg>
          <span aria-live="polite" className="text-sd-sm text-sd-error">{denialMessage || 'Access denied'}</span>
        </div>
      </div>
    );
  }

  const toggle = () => setExpanded((prev) => !prev);

  return (
    <div className={`p-sd-2 rounded-sd-md bg-sd-surface-elevated w-full ${className}`.trim()}>
      <div
        role="button"
        aria-expanded={expanded}
        aria-label={summary}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        className="flex items-center gap-sd-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 rounded-sd-sm"
      >
        <svg className="w-4 h-4 text-sd-text-muted shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="7" width="10" height="7" rx="1" />
          <path d="M5 7V5a3 3 0 016 0v2" />
        </svg>
        <span className="text-sd-sm text-sd-text-secondary">{summary}</span>
      </div>
      <div className={`overflow-hidden transition-[max-height,opacity] duration-150 ease-in-out motion-reduce:transition-none ${expanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="mt-sd-2">
          <div className="overflow-x-auto">
            <table className="w-full text-left" aria-labelledby={titleId}>
              <caption id={titleId} className="sr-only">Requested API access</caption>
              <thead>
                <tr className="border-b border-sd-border">
                  <th scope="col" className="text-sd-sm font-medium text-sd-text-secondary pb-sd-1 pr-sd-2">System</th>
                  <th scope="col" className="text-sd-sm font-medium text-sd-text-secondary pb-sd-1 pr-sd-2">Permission</th>
                  <th scope="col" className="text-sd-sm font-medium text-sd-text-secondary pb-sd-1">Expiry</th>
                </tr>
              </thead>
              <tbody>
                {apiRequests.map((req, i) => (
                  <tr key={i} className="border-b border-sd-border last:border-b-0">
                    <td className="text-sd-sm text-sd-text py-sd-1 pr-sd-2">{req.system}</td>
                    <td className="text-sd-sm py-sd-1 pr-sd-2">
                      <span className={`text-sd-xs font-medium ${req.permission === 'read' ? 'text-sd-info' : 'text-sd-warning'}`}>
                        {req.permission === 'read' ? 'Read-only' : 'Read-Write'}
                      </span>
                    </td>
                    <td className="text-sd-sm text-sd-text py-sd-1">{req.expiry}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sd-sm text-sd-text-secondary mt-sd-2">{justification}</p>
          {mfaRequired && (
            <div className="mt-sd-2">
              <label htmlFor={`${titleId}-mfa`} className="text-sd-sm text-sd-text-secondary">
                Enter verification code
              </label>
              <Input
                id={`${titleId}-mfa`}
                placeholder="000000"
                value={mfaCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setMfaCode(val);
                  if (val.length === 6) {
                    onMfaSubmit?.(val);
                  }
                }}
                className="max-w-[200px] mt-sd-1"
              />
            </div>
          )}
          <div className="flex gap-sd-2 mt-sd-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onApprove?.(); }}
              className="flex-1 min-h-[44px]"
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDeny?.(); }}
              className="flex-1 min-h-[44px]"
            >
              Deny
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
